import React, { useCallback } from 'react'
import dayjs from 'dayjs'
import EmailIcon from '@mui/icons-material/Email'
import DescriptionIcon from '@mui/icons-material/Description'
import { Checkbox, Grid, TableRow, Typography, Tooltip, Box } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { IconBrandLinkedin, IconBrandGoogle, IconBrandX } from '@tabler/icons-react'
import EnhancedTableCell from '../EnhancedTableCell/EnhancedTableCell'
import classes from './style.module.scss'
import { leadStatuses } from '../../../../constants'
import { dialogToggle } from '../../../../store/slices/dialog'
import { useDispatch, useSelector } from '../../../../store/store'
import { hasPermission } from '../../../../utils/tools'
import useApplicantPrivacy from '../../../../hooks/useApplicantPrivacy'

// Platform mapping for icons - maps platform value to SVG filename
const platformIconMap: { [key: string]: string } = {
  facebook: 'fb',
  instagram: 'ig',
  internalRecruspin: 'recruspin',
  tiktok: 'tiktok',
  spotify: 'spotify',
  youtube: 'youtube',
  // Legacy mappings
  fb: 'fb',
  ig: 'ig',
  // Audience Network and Messenger are Meta/Facebook surfaces -> use the Facebook icon
  an: 'fb',
  msg: 'fb',
  // For platforms without SVG files, we'll use Tabler icons in the component
}

interface LeadsRowProps {
  labelId?: string
  row: {
    id: string
    status: string
    createdAt: string
    firstName: string
    lastName: string
    phone: string
    email: string
    platform: string
    emailHistory?: Array<{
      id: string
      type: string
      status: string
      createdAt: string
      body?: string
    }>
    applicantCV?: {
      id: string
      name: string
      createdAt: string
    } | null
  }
  isItemSelected?: boolean
  checkbox?: boolean
  customClickHandler?: (id: string) => void
}

const LeadsRow: React.FC<LeadsRowProps> = ({
  labelId = '',
  row,
  isItemSelected = false,
  checkbox = false,
  customClickHandler,
}) => {
  const {
    id,
    status,
    createdAt,
    firstName,
    lastName,
    phone,
    email,
    platform,
    emailHistory,
    applicantCV,
  } = row

  // This column tracks the CV request to the applicant. emailHistory also holds other emails
  // (e.g. the new lead notification to the customer), which must not light it up.
  const hasEmailSent = !!emailHistory?.some((email) => email.type === 'askForCV')

  // Check if CV was uploaded
  const hasCVUploaded = applicantCV !== null && applicantCV !== undefined

  const [, setSearchParams] = useSearchParams()
  const dispatch = useDispatch()

  const { permissions } = useSelector((state) => state.user)
  const permission = hasPermission(permissions?.leadsAndTalentsDetailsFields)
  const { maskName, maskEmail, maskPhone } = useApplicantPrivacy()

  const handleClick = useCallback(() => {
    if (customClickHandler) {
      customClickHandler(id)
    } else {
      dispatch(dialogToggle(true))
      // Keep the other params (e.g. `legacy`, see LegacyCampaignRedirect).
      setSearchParams((params) => {
        params.set('id', id)
        return params
      })
    }
  }, [dispatch, id, setSearchParams, customClickHandler])

  const TableCellLead = (
    props: JSX.IntrinsicAttributes & {
      children: React.ReactNode
      action?: boolean | undefined
      checkbox?: boolean | undefined
      handleClick?: (() => void) | undefined
    }
  ) => {
    return (
      <EnhancedTableCell id={id} handleClick={handleClick} {...props}>
        {props.children}
      </EnhancedTableCell>
    )
  }

  const statusValueToLabel = React.useCallback((value: string) => {
    return leadStatuses.find((status) => status.value === value)?.label
  }, [])

  // Get the correct icon for the platform - returns SVG filename or icon component
  const getPlatformIcon = (platform: string) => {
    // Check if there's an SVG file for this platform
    if (platformIconMap[platform]) {
      return (
        <img
          src={`/socials/${platformIconMap[platform]}.svg`}
          alt="Platform icon"
          width="16px"
          height="16px"
        />
      )
    }

    // Otherwise, use Tabler icons
    const iconProps = { size: 16, strokeWidth: 1.5 }
    switch (platform) {
      case 'linkedin':
        return <IconBrandLinkedin {...iconProps} />
      case 'google':
        return <IconBrandGoogle {...iconProps} />
      case 'x':
        return <IconBrandX {...iconProps} />
      default:
        return null
    }
  }

  // Get a formatted platform display name
  const getPlatformDisplayName = (platform: string) => {
    if (!platform) return 'Unknown'

    // Special case for internalRecruspin to show as Landingpage
    if (platform === 'internalRecruspin') return 'Landingpage'

    // Special cases
    if (platform === 'x') return 'X'
    if (platform === 'linkedin') return 'LinkedIn'
    if (platform === 'youtube') return 'YouTube'
    if (platform === 'tiktok') return 'TikTok'
    // Meta raw platform codes + Facebook surfaces (Audience Network, Messenger)
    if (platform === 'fb' || platform === 'an' || platform === 'msg') return 'Facebook'
    if (platform === 'ig') return 'Instagram'

    // Capitalize first letter
    return platform.charAt(0).toUpperCase() + platform.slice(1)
  }

  return (
    <TableRow
      hover
      role="checkbox"
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={id}
      selected={isItemSelected}
      sx={{ cursor: 'pointer' }}
    >
      {checkbox && (
        <TableCellLead checkbox action>
          <Checkbox
            color="primary"
            checked={isItemSelected}
            inputProps={{
              'aria-labelledby': labelId,
            }}
          />
        </TableCellLead>
      )}
      {permission('status') && (
        <TableCellLead>
          <p className={`${classes.status} ${classes[status]}`}>{statusValueToLabel(status)}</p>
        </TableCellLead>
      )}
      {permission('createdTime') && (
        <TableCellLead>{dayjs(createdAt).format('DD/MM/YYYY')}</TableCellLead>
      )}
      {permission('firstName') && <TableCellLead>{maskName(firstName)}</TableCellLead>}
      {permission('lastName') && <TableCellLead>{maskName(lastName)}</TableCellLead>}
      {permission('phone') && <TableCellLead>{maskPhone(phone)}</TableCellLead>}
      {permission('email') && <TableCellLead>{maskEmail(email)}</TableCellLead>}
      {(permission('platform') || permission('channel')) && (
        <TableCellLead>
          <Box
            sx={{
              display: 'flex',
              padding: '4px 8px',
              alignItems: 'center',
              borderRadius: '100px',
              background: '#E9E7F2',
              width: 'fit-content',
              gap: '4px',
            }}
          >
            {getPlatformIcon(platform)}
            <Typography style={{ margin: 0, whiteSpace: 'pre' }}>
              {getPlatformDisplayName(platform)}
            </Typography>
          </Box>
        </TableCellLead>
      )}
      <TableCellLead>
        {hasEmailSent ? (
          <Tooltip title="Email Sent" arrow>
            <Grid
              container
              alignItems="center"
              justifyContent="center"
              sx={{
                display: 'flex',
                padding: '8px',
                alignItems: 'center',
                borderRadius: '50%',
                background: '#E8F5E8',
                width: '36px',
                height: '36px',
              }}
            >
              <EmailIcon
                sx={{
                  fontSize: '18px',
                  color: '#4CAF50',
                }}
              />
            </Grid>
          </Tooltip>
        ) : (
          <Typography sx={{ color: '#BDBDBD', textAlign: 'center' }}>-</Typography>
        )}
      </TableCellLead>
      <TableCellLead>
        {hasCVUploaded ? (
          <Tooltip title="CV Uploaded" arrow>
            <Grid
              container
              alignItems="center"
              justifyContent="center"
              sx={{
                display: 'flex',
                padding: '8px',
                alignItems: 'center',
                borderRadius: '50%',
                background: '#E3F2FD',
                width: '36px',
                height: '36px',
              }}
            >
              <DescriptionIcon
                sx={{
                  fontSize: '18px',
                  color: '#2196F3',
                }}
              />
            </Grid>
          </Tooltip>
        ) : (
          <Typography sx={{ color: '#BDBDBD', textAlign: 'center' }}>-</Typography>
        )}
      </TableCellLead>
    </TableRow>
  )
}

export default LeadsRow
